/**
 * Capacitor Mobile Utilities
 * Helper functions for mobile-specific features including Geolocation
 */

// Try to import Capacitor modules, fallback to mocks if not available
let Capacitor, App, SplashScreen, StatusBar, Network, Haptics, Toast, Geolocation;
let Style, ImpactStyle;

try {
  // Attempt to import real Capacitor modules
  const CapacitorCore = require('@capacitor/core');
  Capacitor = CapacitorCore.Capacitor;
  
  const AppModule = require('@capacitor/app');
  App = AppModule.App;
  
  const SplashScreenModule = require('@capacitor/splash-screen');
  SplashScreen = SplashScreenModule.SplashScreen;
  
  const StatusBarModule = require('@capacitor/status-bar');
  StatusBar = StatusBarModule.StatusBar;
  Style = StatusBarModule.Style;
  
  const NetworkModule = require('@capacitor/network');
  Network = NetworkModule.Network;
  
  const HapticsModule = require('@capacitor/haptics');
  Haptics = HapticsModule.Haptics;
  ImpactStyle = HapticsModule.ImpactStyle;
  
  const ToastModule = require('@capacitor/toast');
  Toast = ToastModule.Toast;
  
  const GeolocationModule = require('@capacitor/geolocation');
  Geolocation = GeolocationModule.Geolocation;
  
  console.log('✅ Capacitor modules loaded successfully');
} catch (error) {
  console.warn('⚠️ Capacitor not available, using fallback implementations:', error.message);
  
  // Mock Capacitor objects for when packages aren't installed
  Capacitor = {
    isNativePlatform: () => false,
    getPlatform: () => 'web'
  };
  
  App = {
    addListener: () => {},
    exitApp: () => {},
    minimizeApp: () => {},
    getInfo: async () => ({ name: 'App', version: '1.0.0', build: '1' })
  };
  
  SplashScreen = {
    hide: async () => {}
  };
  
  StatusBar = {
    setStyle: async () => {},
    setBackgroundColor: async () => {}
  };
  
  Style = {
    Light: 'LIGHT',
    Dark: 'DARK'
  };
  
  Network = {
    addListener: () => {},
    getStatus: async () => ({ connected: true, connectionType: 'wifi' })
  };
  
  Haptics = {
    impact: async () => {}
  };
  
  ImpactStyle = {
    Light: 'LIGHT',
    Medium: 'MEDIUM',
    Heavy: 'HEAVY'
  };
  
  Toast = {
    show: async () => {}
  };
  
  Geolocation = {
    checkPermissions: async () => ({ location: 'granted', coarseLocation: 'granted' }),
    requestPermissions: async () => ({ location: 'granted', coarseLocation: 'granted' }),
    getCurrentPosition: async () => ({
      coords: {
        latitude: 0,
        longitude: 0,
        accuracy: 0,
        altitude: null,
        altitudeAccuracy: null,
        heading: null,
        speed: null
      },
      timestamp: Date.now()
    }),
    watchPosition: () => null,
    clearWatch: () => {}
  };
}

/**
 * Check if app is running on a native mobile platform
 */
export const isNativePlatform = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Get the current platform (web, ios, android)
 */
export const getPlatform = () => {
  return Capacitor.getPlatform();
};

/**
 * Initialize mobile app features
 * Call this in your App.js useEffect
 */
export const initializeMobileApp = async () => {
  if (!isNativePlatform()) {
    console.log('Running on web platform - mobile features disabled');
    return;
  }

  try {
    console.log(`Initializing mobile app on ${getPlatform()}`);

    // Hide splash screen after app is ready
    await SplashScreen.hide();

    // Configure status bar
    await StatusBar.setStyle({ style: Style.Light });
    await StatusBar.setBackgroundColor({ color: '#7c3aed' }); // Purple theme

    // Set up app state listeners
    App.addListener('appStateChange', ({ isActive }) => {
      console.log('App state changed. Active:', isActive);
    });

    // Set up network listeners (UI is handled by the persistent <NetworkBanner/>)
    Network.addListener('networkStatusChange', status => {
      console.log('Network status changed', status);
    });

    // Request location permissions automatically on app startup
    // This ensures location permissions are requested when the app is first installed
    try {
      console.log('📍 Checking location permissions...');
      const locationPermission = await checkLocationPermissions();
      
      if (locationPermission.location !== 'granted') {
        console.log('📍 Location permission not granted, requesting...');
        // Wait a bit for the app to fully load before requesting permissions
        setTimeout(async () => {
          try {
            const result = await requestLocationPermissions();
            if (result.location === 'granted') {
              console.log('✅ Location permission granted on app startup');
            } else {
              console.log('⚠️ Location permission denied on app startup');
            }
          } catch (err) {
            console.error('Error requesting location permission on startup:', err);
          }
        }, 1000); // Wait 1 second before requesting
      } else {
        console.log('✅ Location permission already granted');
      }
    } catch (locationError) {
      console.error('Error checking/requesting location permissions:', locationError);
      // Don't block app initialization if location permission request fails
    }

    console.log('Mobile app initialized successfully');
  } catch (error) {
    console.error('Error initializing mobile app:', error);
  }
};

/**
 * Get current network status
 */
export const getNetworkStatus = async () => {
  if (!isNativePlatform()) return { connected: true, connectionType: 'wifi' };
  
  try {
    const status = await Network.getStatus();
    return status;
  } catch (error) {
    console.error('Error getting network status:', error);
    return { connected: true, connectionType: 'unknown' };
  }
};

/**
 * Show native toast notification
 */
export const showToast = async (message, duration = 'short') => {
  if (!isNativePlatform()) {
    console.log('Toast (web):', message);
    return;
  }

  try {
    await Toast.show({
      text: message,
      duration: duration === 'long' ? 'long' : 'short',
      position: 'bottom',
    });
  } catch (error) {
    console.error('Error showing toast:', error);
  }
};

/**
 * Trigger haptic feedback
 */
export const triggerHaptic = async (style = 'medium') => {
  if (!isNativePlatform()) return;

  try {
    const impactStyle = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    }[style] || ImpactStyle.Medium;

    await Haptics.impact({ style: impactStyle });
  } catch (error) {
    console.error('Error triggering haptic:', error);
  }
};

/**
 * Get app information
 */
export const getAppInfo = async () => {
  if (!isNativePlatform()) {
    return {
      name: 'Employee Attendance',
      version: '2.0.0',
      build: '1',
      platform: 'web',
    };
  }

  try {
    const info = await App.getInfo();
    return {
      name: info.name,
      version: info.version,
      build: info.build,
      platform: getPlatform(),
    };
  } catch (error) {
    console.error('Error getting app info:', error);
    return null;
  }
};

/**
 * Handle back button press (Android)
 */
export const setupBackButtonHandler = (callback) => {
  if (!isNativePlatform() || getPlatform() !== 'android') return;

  App.addListener('backButton', ({ canGoBack }) => {
    if (callback) {
      callback(canGoBack);
    } else if (!canGoBack) {
      App.exitApp();
    } else {
      window.history.back();
    }
  });
};

/**
 * Check if app has internet connectivity
 */
export const checkConnectivity = async () => {
  const status = await getNetworkStatus();
  return status.connected;
};

/**
 * Exit the app (Android only)
 */
export const exitApp = async () => {
  if (!isNativePlatform() || getPlatform() !== 'android') return;
  
  try {
    await App.exitApp();
  } catch (error) {
    console.error('Error exiting app:', error);
  }
};

/**
 * Minimize the app (move to background)
 */
export const minimizeApp = async () => {
  if (!isNativePlatform()) return;
  
  try {
    await App.minimizeApp();
  } catch (error) {
    console.error('Error minimizing app:', error);
  }
};

// ============================================
// GEOLOCATION FUNCTIONS
// ============================================

/**
 * Check current location permission status
 * @returns {Promise<Object>} Permission status { location: 'granted'|'denied'|'prompt', coarseLocation: 'granted'|'denied'|'prompt' }
 */
export const checkLocationPermissions = async () => {
  try {
    if (!isNativePlatform()) {
      // For web, check if geolocation is available
      if ('geolocation' in navigator) {
        return { location: 'prompt', coarseLocation: 'prompt' };
      }
      return { location: 'denied', coarseLocation: 'denied' };
    }

    // Check if Geolocation is available before checking permissions
    if (!Geolocation || typeof Geolocation.checkPermissions !== 'function') {
      console.warn('Geolocation plugin not available');
      return { location: 'denied', coarseLocation: 'denied' };
    }

    const permissions = await Geolocation.checkPermissions();
    console.log('📍 Location permissions status:', permissions);
    
    // Ensure we return a valid object
    if (permissions && typeof permissions === 'object') {
      return permissions;
    }
    
    return { location: 'denied', coarseLocation: 'denied' };
  } catch (error) {
    // Enhanced error handling
    const errorMessage = error?.message || String(error);
    
    if (errorMessage.includes('SecurityException') || 
        errorMessage.includes('not declared') ||
        errorMessage.includes('permission')) {
      console.error('Location permissions not properly configured in AndroidManifest.xml');
    } else {
      console.error('Error checking location permissions:', error);
    }
    
    return { location: 'denied', coarseLocation: 'denied' };
  }
};

/**
 * Request location permissions from the user
 * @returns {Promise<Object>} Permission status after request
 */
export const requestLocationPermissions = async () => {
  try {
    if (!isNativePlatform()) {
      console.log('Web platform - location permissions handled by browser');
      return { location: 'granted', coarseLocation: 'granted' };
    }

    // Check if Geolocation is available before requesting
    if (!Geolocation || typeof Geolocation.requestPermissions !== 'function') {
      console.warn('Geolocation plugin not available - permissions may not be declared in AndroidManifest.xml');
      return { location: 'denied', coarseLocation: 'denied' };
    }

    console.log('📍 Requesting location permissions...');
    
    // Wrap in try-catch to handle any native bridge errors
    let permissions;
    try {
      permissions = await Geolocation.requestPermissions();
    } catch (nativeError) {
      // Handle specific Android errors
      if (nativeError.message && (
        nativeError.message.includes('permission') ||
        nativeError.message.includes('SecurityException') ||
        nativeError.message.includes('not declared')
      )) {
        console.error('Location permission not declared in AndroidManifest.xml:', nativeError);
        // Don't show toast for this error as it's a configuration issue
        return { location: 'denied', coarseLocation: 'denied' };
      }
      throw nativeError; // Re-throw if it's a different error
    }
    
    console.log('📍 Location permissions result:', permissions);
    
    // Only show toast if permissions object is valid
    if (permissions && typeof permissions === 'object') {
      if (permissions.location === 'granted') {
        try {
          await showToast('Location access granted', 'short');
          await triggerHaptic('light');
        } catch (toastError) {
          // Ignore toast errors - non-critical
          console.warn('Could not show toast:', toastError);
        }
      } else {
        try {
          await showToast('Location access denied', 'short');
        } catch (toastError) {
          // Ignore toast errors - non-critical
          console.warn('Could not show toast:', toastError);
        }
      }
    }
    
    return permissions || { location: 'denied', coarseLocation: 'denied' };
  } catch (error) {
    // Enhanced error handling to prevent crashes
    console.error('Error requesting location permissions:', error);
    
    // Check for specific error types
    const errorMessage = error?.message || String(error);
    
    if (errorMessage.includes('SecurityException') || 
        errorMessage.includes('not declared') ||
        errorMessage.includes('permission')) {
      console.error('Location permissions not properly configured in AndroidManifest.xml');
      // Return denied without showing toast to avoid further errors
      return { location: 'denied', coarseLocation: 'denied' };
    }
    
    // For other errors, try to show toast but don't fail if it doesn't work
    try {
      await showToast('Failed to request location permissions', 'short');
    } catch (toastError) {
      // Ignore toast errors
      console.warn('Could not show error toast:', toastError);
    }
    
    return { location: 'denied', coarseLocation: 'denied' };
  }
};

/**
 * Get current location coordinates
 * @param {Object} options - Geolocation options { enableHighAccuracy, timeout, maximumAge }
 * @returns {Promise<Object>} Position object with coords and timestamp
 */
export const getCurrentLocation = async (options = {}) => {
  const defaultOptions = {
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 0
  };

  const geoOptions = { ...defaultOptions, ...options };

  try {
    if (!isNativePlatform()) {
      // Use browser geolocation API for web
      return new Promise((resolve, reject) => {
        if (!('geolocation' in navigator)) {
          reject(new Error('Geolocation not supported'));
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              coords: {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                altitude: position.coords.altitude,
                altitudeAccuracy: position.coords.altitudeAccuracy,
                heading: position.coords.heading,
                speed: position.coords.speed
              },
              timestamp: position.timestamp
            });
          },
          (error) => {
            reject(error);
          },
          geoOptions
        );
      });
    }

    // Use Capacitor Geolocation for native platforms
    // Check if Geolocation is available
    if (!Geolocation || typeof Geolocation.getCurrentPosition !== 'function') {
      throw new Error('Geolocation plugin not available - permissions may not be declared in AndroidManifest.xml');
    }

    console.log('📍 Getting current location with options:', geoOptions);
    const position = await Geolocation.getCurrentPosition(geoOptions);
    console.log('📍 Current location:', position.coords);
    
    return position;
  } catch (error) {
    console.error('Error getting current location:', error);
    throw error;
  }
};

/**
 * Get current location with automatic permission handling
 * @param {Object} options - Geolocation options
 * @returns {Promise<Object>} Position object or null if permission denied
 */
export const getLocationWithPermission = async (options = {}) => {
  try {
    // Check current permissions
    const permissionStatus = await checkLocationPermissions();
    
    // If denied permanently, return null
    if (permissionStatus.location === 'denied') {
      console.warn('Location permission denied');
      await showToast('Location permission is required for check-in', 'long');
      return null;
    }

    // If not granted, request permissions
    if (permissionStatus.location !== 'granted') {
      const requestResult = await requestLocationPermissions();
      
      if (requestResult.location !== 'granted') {
        console.warn('User denied location permission');
        return null;
      }
    }

    // Get location
    const position = await getCurrentLocation(options);
    return position;
  } catch (error) {
    console.error('Error getting location with permission:', error);
    
    // Provide user-friendly error messages
    if (error.message.includes('timeout')) {
      await showToast('Location request timed out. Please try again.', 'long');
    } else if (error.message.includes('denied')) {
      await showToast('Location permission denied', 'long');
    } else {
      await showToast('Failed to get location. Please check your GPS.', 'long');
    }
    
    throw error;
  }
};

/**
 * Watch position changes
 * @param {Function} callback - Called with position updates
 * @param {Object} options - Geolocation options
 * @returns {String} Watch ID (use with clearLocationWatch)
 */
export const watchLocation = async (callback, options = {}) => {
  const defaultOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0
  };

  const geoOptions = { ...defaultOptions, ...options };

  try {
    if (!isNativePlatform()) {
      // Use browser geolocation API
      if (!('geolocation' in navigator)) {
        throw new Error('Geolocation not supported');
      }

      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          callback({
            coords: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              altitude: position.coords.altitude,
              altitudeAccuracy: position.coords.altitudeAccuracy,
              heading: position.coords.heading,
              speed: position.coords.speed
            },
            timestamp: position.timestamp
          });
        },
        (error) => {
          console.error('Watch location error:', error);
        },
        geoOptions
      );

      return watchId;
    }

    // Use Capacitor Geolocation
    const watchId = await Geolocation.watchPosition(geoOptions, callback);
    return watchId;
  } catch (error) {
    console.error('Error watching location:', error);
    throw error;
  }
};

/**
 * Clear location watch
 * @param {String} watchId - Watch ID from watchLocation
 */
export const clearLocationWatch = async (watchId) => {
  try {
    if (!watchId) return;

    if (!isNativePlatform()) {
      navigator.geolocation.clearWatch(watchId);
      return;
    }

    await Geolocation.clearWatch({ id: watchId });
  } catch (error) {
    console.error('Error clearing location watch:', error);
  }
};

// Export a default object with all utilities
const CapacitorUtils = {
  // Platform
  isNativePlatform,
  getPlatform,
  initializeMobileApp,
  
  // Network
  getNetworkStatus,
  checkConnectivity,
  
  // UI Feedback
  showToast,
  triggerHaptic,
  
  // App Management
  getAppInfo,
  setupBackButtonHandler,
  exitApp,
  minimizeApp,
  
  // Geolocation
  checkLocationPermissions,
  requestLocationPermissions,
  getCurrentLocation,
  getLocationWithPermission,
  watchLocation,
  clearLocationWatch,
};

export default CapacitorUtils;
